import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { DrawerIcon } from '../../../../assets/icons/DrawerIcon';
import { EditIcon } from '../../../../assets/icons/EditIcon';
import { SaveIcon } from '../../../../assets/icons/SaveIcon';
import { BrandInfo } from '../../../../assets/icons/BrandInfo';
import VendorManagement from '../VendorManagement';
import ToggleRow from '../../../Shared/ToggleRow';
import StatusToggle from '../../../Shared/StatusToggle';
import CollapsibleSection from '../../../Shared/CollapsibleSection';
import ImageUploadButton from '../../../Shared/ImageUploadButton';
import * as adminHandlers from '../../../../routes/Admin/redux/handlers';

const Subsection = ({ label, children }) => (
    <div className="space-y-2">
        <p className="text-xs text-zinc-500 uppercase tracking-wide">{label}</p>
        {children}
    </div>
);

const Field = ({ label, children }) => (
    <div className="space-y-1">
        <p className="text-xs text-zinc-400">{label}</p>
        {children}
    </div>
);

const SubBrandModal = ({ subBrand, isOpen, onClose, onUpdate, brandId }) => {
    const dispatch = useDispatch();
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedSubBrand, setEditedSubBrand] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        if (subBrand) {
            // Reset state when subBrand prop changes
            setEditedSubBrand(null);
            setIsEditMode(false);
            setValidationErrors({});
        }
    }, [subBrand?.id]); // React to subBrand ID changes
    
    // Update internal state when subBrand prop is updated
    useEffect(() => {
        if (subBrand && !isEditMode && !editedSubBrand) {
            // Force re-render with updated data
        }
    }, [subBrand]);

    const handleEditClick = () => {
        setIsEditMode(true);
        setEditedSubBrand({
            ...subBrand
        });
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditedSubBrand(null);
        setValidationErrors({});
    };

    const handleInputChange = (field, value) => {
        setEditedSubBrand(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear validation error for the field if it exists
        setValidationErrors(prev => {
            if (prev[field]) {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            }
            return prev;
        });
    };

    const handleArrayItemChange = (field, index, value) => {
        setEditedSubBrand(prev => ({
            ...prev,
            [field]: prev[field].map((item, i) => i === index ? value : item)
        }));
    };

    const handleArrayItemAdd = (field) => {
        setEditedSubBrand(prev => ({
            ...prev,
            [field]: [...(prev[field] || []), '']
        }));
    };

    const handleArrayItemRemove = (field, index) => {
        setEditedSubBrand(prev => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index)
        }));
    };

    const handleArrayReorder = (field, fromIndex, toIndex) => {
        if (fromIndex === toIndex) return;
        setEditedSubBrand(prev => {
            const arr = [...(prev[field] || [])];
            const [moved] = arr.splice(fromIndex, 1);
            arr.splice(toIndex, 0, moved);
            return { ...prev, [field]: arr };
        });
    };

    const [dragState, setDragState] = useState({ field: null, from: null, over: null });


    const validateForm = () => {
        const errors = {};
        
        if (!editedSubBrand?.name?.trim()) {
            errors.name = 'Sub-brand name is required';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);
        try {
            const updatedData = {
                name: editedSubBrand.name,
                description: editedSubBrand.description || '',
                is_active: editedSubBrand.is_active,
                is_pin: editedSubBrand.is_pin,
                is_wallet: editedSubBrand.is_wallet,
                redirect_link: editedSubBrand.redirect_link || '',
                hex_code_bg: editedSubBrand.hex_code_bg || '',
                hex_code_text: editedSubBrand.hex_code_text || '',
                how_it_works: editedSubBrand.how_it_works || [],
                terms_and_conditions: editedSubBrand.terms_and_conditions || [],
                excluded: editedSubBrand.excluded || { is_referral: false, is_activation_promotion: false },
                dynamic_details: editedSubBrand.dynamic_details || {}
            };

            // Only send image when it's a freshly picked base64 data URL (not the
            // existing S3 URL the backend already stored).
            if (editedSubBrand.image && editedSubBrand.image.startsWith("data:")) {
                updatedData.image = editedSubBrand.image;
            }

            const result = await dispatch(adminHandlers.updateSubBrandHandler(brandId, subBrand.id, updatedData));
            
            // Update the editedSubBrand with the result to show updated data
            if (result) {
                setEditedSubBrand(result);
            }
            
            setIsEditMode(false);
            
            if (onUpdate) {
                onUpdate(result);
            }
        } catch (error) {
            console.error('Failed to update sub-brand:', error);
        } finally {
            setIsSaving(false);
        }
    };

    if (!subBrand) return null;

    const currentSubBrand = editedSubBrand || subBrand;

    return (
        <div className={`fixed inset-0 z-[60] overflow-hidden ${isOpen ? '' : 'pointer-events-none'}`}>
            {/* Overlay */}
            <div 
                className={`absolute inset-0 bg-black transition-opacity duration-300 ease-in-out ${
                    isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
                }`}
                onClick={onClose}
            />
            
            {/* Modal Panel */}
            <div className={`fixed right-0 top-0 h-full w-[580px] bg-eerie-black shadow-xl flex flex-col transform transition-all duration-300 ease-in-out ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}>
                {/* Header */}
                <div className="flex flex-col">
                    <div className='border-b border-zinc-700'>
                        <button
                            onClick={onClose}
                            className="mx-4 my-3 bg-charleston-green rounded-lg transition-colors hover:bg-zinc-800"
                        >
                            <DrawerIcon width={38} height={34} />
                        </button>
                    </div>

                    <div className="flex flex-col px-4 py-3">
                        <div className="flex justify-between items-center">
                            <h2 className="text-3xl text-white">Sub-Brand Details</h2>
                            {!isEditMode ? (
                                <button 
                                    onClick={handleEditClick}
                                    className="px-4 py-2 bg-charleston-green text-white border border-zinc-700 rounded-full hover:bg-zinc-800 flex items-center justify-center space-x-2"
                                >
                                    <EditIcon width={20} height={20} />
                                    <span className="text-sm">Edit Sub-Brand</span>
                                </button>
                            ) : (
                                <div className="flex space-x-2">
                                    {!isSaving && (
                                        <button 
                                            onClick={handleCancelEdit}
                                            className="px-4 py-2 bg-charleston-green text-white border border-zinc-700 rounded-full hover:bg-zinc-800" 
                                        >
                                            <span className="text-sm">Cancel</span>
                                        </button>
                                    )}
                                    <button 
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className={`px-4 py-2 text-white border rounded-full flex items-center justify-center space-x-2 ${
                                            isSaving 
                                                ? "bg-gray-600 border-gray-600 cursor-not-allowed" 
                                                : "bg-green-600 border-green-600 hover:bg-green-500"
                                        }`}
                                    >
                                        {isSaving ? (
                                            <>
                                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span className="text-sm">Saving...</span>
                                            </>
                                        ) : (
                                            <>
                                                <SaveIcon width={20} height={20} />
                                                <span className="text-sm">Save Changes</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                        {isEditMode && Object.values(validationErrors).filter(Boolean).length > 0 && (
                            <div className="mt-2 p-3 bg-red-950 border border-red-800 rounded-lg">
                                <p className="text-red-400 text-sm font-medium mb-2">Please fix the following errors:</p>
                                <ul className="text-red-300 text-xs space-y-1">
                                    {Object.entries(validationErrors)
                                        .filter(([field, error]) => error) // Only show fields with actual error messages
                                        .map(([field, error]) => (
                                            <li key={field} className="flex items-start">
                                                <span className="text-red-500 mr-2">•</span>
                                                <span><strong className="capitalize">{field.replace(/_/g, ' ')}:</strong> {error}</span>
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="px-4 pb-4 pt-2 space-y-6">
                        {/* Sub-Brand Information Section */}
                        <div>
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="bg-charleston-green rounded-lg flex items-center justify-center">
                                    <BrandInfo width={32} height={32} />
                                </div>
                                <h3 className="text-white font-medium">Sub-Brand Information</h3>
                            </div>

                            <div className="space-y-4">
                                {/* Sub-Brand Logo */}
                                <div>
                                    <p className="text-md mb-2">Sub-Brand Logo</p>
                                    <div className="flex items-center space-x-4">
                                        <div className="w-20 h-20 bg-white p-0.5 inline-block rounded-lg">
                                            {currentSubBrand.image ? (
                                                <img
                                                    src={currentSubBrand.image}
                                                    alt={currentSubBrand.name}
                                                    className="w-full h-full object-contain rounded-lg"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-zinc-300 rounded-lg" />
                                            )}
                                        </div>
                                        {isEditMode && (
                                            <ImageUploadButton
                                                onPick={(dataUrl) => handleInputChange("image", dataUrl)}
                                                onReset={() => handleInputChange("image", subBrand?.image || "")}
                                                canReset={!!editedSubBrand?.image && editedSubBrand.image !== subBrand?.image}
                                                label="Change Logo"
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Name */}
                                <div>
                                    <p className="text-md mb-1">Sub-Brand Name</p>
                                    <input
                                        type="text"
                                        value={currentSubBrand.name || ''}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        readOnly={!isEditMode}
                                        className={`w-full bg-charleston-green border rounded-lg px-3 py-2 text-white text-sm ${
                                            validationErrors.name ? 'border-red-500' : 'border-zinc-700'
                                        } ${isEditMode ? 'focus:border-blue-500 focus:outline-none' : ''}`}
                                    />
                                    {validationErrors.name && (
                                        <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>
                                    )}
                                </div>

                                {/* Description */}
                                <div>
                                    <p className="text-md mb-1">Description</p>
                                    <textarea
                                        value={currentSubBrand.description || ''}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        readOnly={!isEditMode}
                                        rows={3}
                                        className={`w-full bg-charleston-green border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm ${
                                            isEditMode ? 'focus:border-blue-500 focus:outline-none' : ''
                                        }`}
                                    />
                                </div>


                                {/* Redirect Link */}
                                <div>
                                    <p className="text-md mb-1">Redirect Link</p>
                                    <input
                                        type="url"
                                        value={currentSubBrand.redirect_link || ''}
                                        onChange={(e) => handleInputChange('redirect_link', e.target.value)}
                                        readOnly={!isEditMode}
                                        className={`w-full bg-charleston-green border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm ${
                                            isEditMode ? 'focus:border-blue-500 focus:outline-none' : ''
                                        }`}
                                    />
                                </div>

                                {/* Color Codes */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-md mb-1">Background Color</p>
                                        <input
                                            type="text"
                                            value={currentSubBrand.hex_code_bg || ''}
                                            onChange={(e) => handleInputChange('hex_code_bg', e.target.value)}
                                            readOnly={!isEditMode}
                                            placeholder="#000000"
                                            className={`w-full bg-charleston-green border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm ${
                                                isEditMode ? 'focus:border-blue-500 focus:outline-none' : ''
                                            }`}
                                        />
                                    </div>
                                    <div>
                                        <p className="text-md mb-1">Text Color</p>
                                        <input
                                            type="text"
                                            value={currentSubBrand.hex_code_text || ''}
                                            onChange={(e) => handleInputChange('hex_code_text', e.target.value)}
                                            readOnly={!isEditMode}
                                            placeholder="#FFFFFF"
                                            className={`w-full bg-charleston-green border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm ${
                                                isEditMode ? 'focus:border-blue-500 focus:outline-none' : ''
                                            }`}
                                        />
                                    </div>
                                </div>

                                {/* Status */}
                                <div>
                                    <p className="text-md mb-2">Status</p>
                                    {isEditMode ? (
                                        <StatusToggle
                                            checked={!!currentSubBrand.is_active}
                                            onChange={(v) => handleInputChange('is_active', v)}
                                        />
                                    ) : (
                                        <span className={`inline-block px-4 py-1.5 text-xs rounded-full ${
                                            currentSubBrand.is_active
                                                ? 'bg-green-950 text-green-500 border border-green-500'
                                                : 'bg-red-950 text-red-500 border border-red-500'
                                        }`}>
                                            {currentSubBrand.is_active ? 'ACTIVE' : 'INACTIVE'}
                                        </span>
                                    )}
                                </div>

                                {/* Flags */}
                                <div>
                                    <p className="text-md mb-2">Order Configuration</p>
                                    <div className="space-y-2">
                                        <ToggleRow
                                            label="Only PIN"
                                            checked={!!currentSubBrand.is_pin}
                                            onChange={(v) => handleInputChange('is_pin', v)}
                                            disabled={!isEditMode}
                                        />
                                        <ToggleRow
                                            label="Wallet Applicable"
                                            checked={!!currentSubBrand.is_wallet}
                                            onChange={(v) => handleInputChange('is_wallet', v)}
                                            disabled={!isEditMode}
                                        />
                                    </div>
                                </div>

                                {/* Excluded Flags */}
                                <div>
                                    <p className="text-md mb-2">Order Reward Exclusion</p>
                                    <div className="space-y-2">
                                        <ToggleRow
                                            label="Referral"
                                            checked={!!currentSubBrand.excluded?.is_referral}
                                            onChange={(v) => handleInputChange('excluded', { ...currentSubBrand.excluded, is_referral: v })}
                                            disabled={!isEditMode}
                                        />
                                        <ToggleRow
                                            label="Activation Promotion"
                                            checked={!!currentSubBrand.excluded?.is_activation_promotion}
                                            onChange={(v) => handleInputChange('excluded', { ...currentSubBrand.excluded, is_activation_promotion: v })}
                                            disabled={!isEditMode}
                                        />
                                    </div>
                                </div>

                                {/* Dynamic Details */}
                                {(() => {
                                    const dd = currentSubBrand.dynamic_details || {};
                                    const card = dd.card_info || {};
                                    const cardEnabled = !!card.is_enabled;
                                    const cardData = card.data || {};
                                    const heading = cardData.heading || {};
                                    const message = cardData.message || {};
                                    const points = Array.isArray(message.points) ? message.points : [];

                                    const updateDetail = (key, next) => {
                                        handleInputChange('dynamic_details', {
                                            ...dd,
                                            [key]: next,
                                        });
                                    };
                                    const toggleDetail = (key, enabled) => {
                                        const detail = dd[key] || {};
                                        updateDetail(key, { ...detail, is_enabled: enabled });
                                    };
                                    const updateCardInfoData = (newData) => {
                                        updateDetail('card_info', { ...card, data: newData });
                                    };
                                    const updateHeadingField = (field, value) => {
                                        updateCardInfoData({ ...cardData, heading: { ...heading, [field]: value } });
                                    };
                                    const updateMessageField = (field, value) => {
                                        updateCardInfoData({ ...cardData, message: { ...message, [field]: value, points } });
                                    };
                                    const updatePoint = (index, field, value) => {
                                        const next = points.map((p, i) => (i === index ? { ...p, [field]: value } : p));
                                        updateCardInfoData({ ...cardData, message: { ...message, points: next } });
                                    };
                                    const addPoint = () => {
                                        updateCardInfoData({
                                            ...cardData,
                                            message: { ...message, points: [...points, { title: '', description: '', image: '' }] },
                                        });
                                    };
                                    const removePoint = (index) => {
                                        updateCardInfoData({
                                            ...cardData,
                                            message: { ...message, points: points.filter((_, i) => i !== index) },
                                        });
                                    };

                                    const inputCls = `w-full bg-charleston-green border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm ${
                                        isEditMode ? 'focus:border-blue-500 focus:outline-none' : ''
                                    }`;

                                    const renderToggle = (keyName, label, flat = false) => (
                                        <ToggleRow
                                            label={label}
                                            checked={!!(dd[keyName] || {}).is_enabled}
                                            onChange={(v) => toggleDetail(keyName, v)}
                                            disabled={!isEditMode}
                                            flat={flat}
                                        />
                                    );

                                    return (
                                        <div>
                                            <p className="text-md mb-2">Dynamic Details</p>
                                            <div className="space-y-2">
                                                {renderToggle('is_clubbable', 'Clubbable')}
                                                {renderToggle('is_balance_reusable', 'Balance Reusable')}
                                            </div>

                                            {/* Card Info — toggle + content as one unit */}
                                            <div className="mt-3 bg-charleston-green border border-zinc-700 rounded-lg">
                                                {renderToggle('card_info', 'Card Info', true)}
                                                {cardEnabled && (
                                                    <div className="border-t border-zinc-700 p-3 space-y-4">
                                                        <CollapsibleSection label="Heading">
                                                            <Field label="Title">
                                                                <input type="text" value={heading.title || ''} readOnly={!isEditMode} onChange={(e) => updateHeadingField('title', e.target.value)} placeholder="Title" className={inputCls} />
                                                            </Field>
                                                            <Field label="Description">
                                                                <textarea value={heading.description || ''} readOnly={!isEditMode} onChange={(e) => updateHeadingField('description', e.target.value)} placeholder="Description" rows={2} className={inputCls} />
                                                            </Field>
                                                            <Field label="Image URL">
                                                                <input type="text" value={heading.image || ''} readOnly={!isEditMode} onChange={(e) => updateHeadingField('image', e.target.value)} placeholder="Image URL" className={inputCls} />
                                                            </Field>
                                                        </CollapsibleSection>

                                                        <CollapsibleSection label="Message">
                                                            <Field label="Title">
                                                                <input type="text" value={message.title || ''} readOnly={!isEditMode} onChange={(e) => updateMessageField('title', e.target.value)} placeholder="Title" className={inputCls} />
                                                            </Field>
                                                            <Field label="Description">
                                                                <textarea value={message.description || ''} readOnly={!isEditMode} onChange={(e) => updateMessageField('description', e.target.value)} placeholder="Description" rows={2} className={inputCls} />
                                                            </Field>
                                                            <Field label="Image URL">
                                                                <input type="text" value={message.image || ''} readOnly={!isEditMode} onChange={(e) => updateMessageField('image', e.target.value)} placeholder="Image URL" className={inputCls} />
                                                            </Field>

                                                            <div className="mt-3 pt-3 pl-3 border-l-2 border-zinc-700 space-y-2">
                                                                <div className="flex justify-between items-center">
                                                                    <p className="text-xs text-zinc-400 uppercase tracking-wide">
                                                                        {`Points${points.length > 0 ? ` (${points.length})` : ''}`}
                                                                    </p>
                                                                    {isEditMode && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={addPoint}
                                                                            className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-500"
                                                                        >
                                                                            + Add Point
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {points.length === 0 ? (
                                                                    <p className="text-zinc-500 text-sm text-center py-2">No points</p>
                                                                ) : (
                                                                    <div className="space-y-2">
                                                                        {points.map((point, index) => (
                                                                            <div key={index} className="flex items-start space-x-2 bg-zinc-800 rounded-lg p-2">
                                                                                <div className="flex-shrink-0 bg-zinc-700 text-zinc-300 text-xs font-medium px-2 py-1 rounded mt-0.5">
                                                                                    {index + 1}
                                                                                </div>
                                                                                <div className="flex-1 space-y-2">
                                                                                    <Field label="Title">
                                                                                        <input type="text" value={point?.title || ''} readOnly={!isEditMode} onChange={(e) => updatePoint(index, 'title', e.target.value)} placeholder="Title" className={inputCls} />
                                                                                    </Field>
                                                                                    <Field label="Description">
                                                                                        <textarea value={point?.description || ''} readOnly={!isEditMode} onChange={(e) => updatePoint(index, 'description', e.target.value)} placeholder="Description" rows={2} className={inputCls} />
                                                                                    </Field>
                                                                                    <Field label="Image URL">
                                                                                        <input type="text" value={point?.image || ''} readOnly={!isEditMode} onChange={(e) => updatePoint(index, 'image', e.target.value)} placeholder="Image URL" className={inputCls} />
                                                                                    </Field>
                                                                                </div>
                                                                                {isEditMode && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => removePoint(index)}
                                                                                        className="flex-shrink-0 text-red-400 hover:text-red-300 text-xs px-2 py-1"
                                                                                    >
                                                                                        Remove
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </CollapsibleSection>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* How It Works */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <p className="text-md">How It Works</p>
                                        {isEditMode && (
                                            <button
                                                type="button"
                                                onClick={() => handleArrayItemAdd('how_it_works')}
                                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-500"
                                            >
                                                + Add Step
                                            </button>
                                        )}
                                    </div>
                                    {!isEditMode ? (
                                        <div className="bg-charleston-green border border-zinc-700 rounded-lg max-h-96 overflow-y-auto">
                                            {(currentSubBrand.how_it_works || []).length > 0 ? (
                                                <div className="p-3 space-y-3">
                                                    {currentSubBrand.how_it_works.map((step, index) => (
                                                        <div key={index} className="flex items-start pb-3 border-b border-zinc-700 last:border-0 last:pb-0">
                                                            <div className="flex-shrink-0 bg-zinc-700 text-zinc-300 text-xs font-medium px-2 py-0.5 rounded mr-3 mt-0.5">
                                                                {index + 1}
                                                            </div>
                                                            <p className="text-zinc-300 text-sm flex-1 leading-relaxed">{step}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-zinc-500 text-sm text-center p-4">No steps defined</p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="bg-charleston-green border border-zinc-700 rounded-lg p-3">
                                            <div className="space-y-2">
                                                {(currentSubBrand.how_it_works || []).map((step, index) => {
                                                    const isOver = dragState.field === 'how_it_works' && dragState.over === index && dragState.from !== index;
                                                    const isDragging = dragState.field === 'how_it_works' && dragState.from === index;
                                                    return (
                                                        <div
                                                            key={index}
                                                            draggable
                                                            onDragStart={() => setDragState({ field: 'how_it_works', from: index, over: index })}
                                                            onDragOver={(e) => { e.preventDefault(); if (dragState.over !== index) setDragState((s) => ({ ...s, over: index })); }}
                                                            onDrop={(e) => { e.preventDefault(); handleArrayReorder('how_it_works', dragState.from, index); setDragState({ field: null, from: null, over: null }); }}
                                                            onDragEnd={() => setDragState({ field: null, from: null, over: null })}
                                                            className={`flex items-center space-x-2 bg-zinc-800 rounded-lg p-2 transition-all ${isDragging ? 'opacity-40' : ''} ${isOver ? 'ring-2 ring-blue-500' : ''}`}
                                                        >
                                                            <div className="flex-shrink-0 cursor-grab active:cursor-grabbing text-zinc-500 hover:text-zinc-300" title="Drag to reorder">
                                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                                    <circle cx="9" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/>
                                                                    <circle cx="15" cy="6" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="15" cy="18" r="1.5"/>
                                                                </svg>
                                                            </div>
                                                            <div className="flex-shrink-0 bg-zinc-700 text-zinc-300 text-xs font-medium px-2 py-1 rounded">
                                                                {index + 1}
                                                            </div>
                                                            <input
                                                                type="text"
                                                                value={step}
                                                                onChange={(e) => handleArrayItemChange('how_it_works', index, e.target.value)}
                                                                className="flex-1 bg-transparent border border-zinc-700 px-2 py-1 text-white text-sm focus:border-blue-500 focus:outline-none rounded"
                                                                placeholder="Enter step description"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => handleArrayItemRemove('how_it_works', index)}
                                                                className="p-1.5 text-red-400 hover:bg-red-950/50 rounded-lg transition-colors"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                                {(!currentSubBrand.how_it_works || currentSubBrand.how_it_works.length === 0) && (
                                                    <div className="text-center py-4 border-2 border-dashed border-zinc-700 rounded-lg">
                                                        <p className="text-zinc-500 text-sm">Click "Add Step" to add instructions</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Terms and Conditions */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <p className="text-md">Terms and Conditions</p>
                                        {isEditMode && (
                                            <button
                                                type="button"
                                                onClick={() => handleArrayItemAdd('terms_and_conditions')}
                                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-500"
                                            >
                                                + Add Term
                                            </button>
                                        )}
                                    </div>
                                    {!isEditMode ? (
                                        <div className="bg-charleston-green border border-zinc-700 rounded-lg max-h-96 overflow-y-auto">
                                            {(currentSubBrand.terms_and_conditions || []).length > 0 ? (
                                                <div className="p-3 space-y-3">
                                                    {currentSubBrand.terms_and_conditions.map((term, index) => (
                                                        <div key={index} className="flex items-start pb-3 border-b border-zinc-700 last:border-0 last:pb-0">
                                                            <div className="flex-shrink-0 bg-zinc-700 text-zinc-300 text-xs font-medium px-2 py-0.5 rounded mr-3 mt-0.5">
                                                                {index + 1}
                                                            </div>
                                                            <p className="text-zinc-300 text-sm flex-1 leading-relaxed">{term}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-zinc-500 text-sm text-center p-4">No terms defined</p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="bg-charleston-green border border-zinc-700 rounded-lg p-3 max-h-96 overflow-y-auto">
                                            <div className="space-y-2">
                                                {(currentSubBrand.terms_and_conditions || []).map((term, index) => {
                                                    const isOver = dragState.field === 'terms_and_conditions' && dragState.over === index && dragState.from !== index;
                                                    const isDragging = dragState.field === 'terms_and_conditions' && dragState.from === index;
                                                    return (
                                                        <div
                                                            key={index}
                                                            draggable
                                                            onDragStart={() => setDragState({ field: 'terms_and_conditions', from: index, over: index })}
                                                            onDragOver={(e) => { e.preventDefault(); if (dragState.over !== index) setDragState((s) => ({ ...s, over: index })); }}
                                                            onDrop={(e) => { e.preventDefault(); handleArrayReorder('terms_and_conditions', dragState.from, index); setDragState({ field: null, from: null, over: null }); }}
                                                            onDragEnd={() => setDragState({ field: null, from: null, over: null })}
                                                            className={`flex items-start space-x-2 bg-zinc-800 rounded-lg p-2 transition-all ${isDragging ? 'opacity-40' : ''} ${isOver ? 'ring-2 ring-blue-500' : ''}`}
                                                        >
                                                            <div className="flex-shrink-0 cursor-grab active:cursor-grabbing text-zinc-500 hover:text-zinc-300 mt-1.5" title="Drag to reorder">
                                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                                    <circle cx="9" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/>
                                                                    <circle cx="15" cy="6" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="15" cy="18" r="1.5"/>
                                                                </svg>
                                                            </div>
                                                            <div className="flex-shrink-0 bg-zinc-700 text-zinc-300 text-xs font-medium px-2 py-1 rounded mt-1.5">
                                                                {index + 1}
                                                            </div>
                                                            <textarea
                                                                value={term}
                                                                onChange={(e) => handleArrayItemChange('terms_and_conditions', index, e.target.value)}
                                                                rows={2}
                                                                className="flex-1 bg-transparent border border-zinc-700 px-2 py-1 text-white text-sm focus:border-blue-500 focus:outline-none resize-none rounded"
                                                                placeholder="Enter term or condition"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => handleArrayItemRemove('terms_and_conditions', index)}
                                                                className="p-1.5 text-red-400 hover:bg-red-950/50 rounded-lg transition-colors mt-1.5"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                                {(!currentSubBrand.terms_and_conditions || currentSubBrand.terms_and_conditions.length === 0) && (
                                                    <div className="text-center py-4 border-2 border-dashed border-zinc-700 rounded-lg">
                                                        <p className="text-zinc-500 text-sm">Click "Add Term" to add terms and conditions</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Vendor Management Section */}
                                <div className="mt-6">
                                    <VendorManagement 
                                        brandId={brandId} 
                                        subBrandId={currentSubBrand.id} 
                                        isEditMode={isEditMode}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubBrandModal;